import { AutosizeModule } from 'ngx-autosize';
import * as ngCommon from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ServiceProxyModule } from '@shared/service-proxies/service-proxy.module';
import { UtilsModule } from '@shared/utils/utils.module';
import { FileUploadModule } from 'ng2-file-upload';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppCommonModule } from './shared/common/app-common.module';
import { BillingModule } from './shared/billing/billing.module';
import { BlobModule } from 'angular-azure-blob-service';
import { InternationalPhoneNumberModule } from 'ngx-international-phone-number';
import { UserSignalrService } from './shared/layout/change-status/user-signalr.service';
import { AngularMultiSelectModule } from 'angular2-multiselect-dropdown';
import {
    PerfectScrollbarModule, PerfectScrollbarConfigInterface,
    PERFECT_SCROLLBAR_CONFIG
} from 'ngx-perfect-scrollbar';
import { NgxPopperModule } from 'ngx-popper';
import { UpgradeSubscriptionComponent } from './main/upgrade-subscription/upgrade-subscription.component';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { AddAltModule, AddModule, ArrowUpRightModule, BuildingModule, CalendarModule, CheckboxCheckedModule, CheckmarkModule, CloseModule, CloudUploadModule, CopyFileModule, DebugModule, FoldersModule, HelpModule, LogoutModule, MoonModule, MovementModule, NotificationModule, PhoneModule, PortfolioModule, SearchModule, SettingsModule, SunModule, TimeModule, UserAvatarModule, UserMultipleModule, WarningModule, WikisModule, RenewModule, Touch_1Module, TrashCanModule, EditModule, IbmSecurityModule, DashboardModule, CatalogModule, ImageModule, DocumentBlankModule } from '@carbon/icons-angular';
import { NoCacheHeadersInterceptor } from '@shared/helpers/NoCacheHeadersHelper';
import { BrowserTabHeaderInterceptor } from '@shared/helpers/BrowserTabHeaderInterceptor';
import { LucideAngularModule, Briefcase, Calendar, CalendarPlus, Sun, Bell, Users, Phone, Moon, Settings, Search, Building, Building2, ChevronRight, CircleUser, File, LayoutGrid, LogOut, Plus, Star, Link, UserPlus, SquareCheck, SquareCheckBig, FileText, Upload, ChevronsRight, ChevronsLeft, ChevronDown, ChevronUp, MessageSquare, User, ArrowRight, PanelLeftOpen, PanelLeftClose, Mail, Video, Globe, MoreVertical, CheckSquare, Pencil, Ellipsis, Sparkles, Zap, CreditCard, BookOpen, AlertCircle, Receipt, Check, X, Shield, Cloud, Reply, Kanban } from 'lucide-angular';
import {
    MsalModule,
    MsalService,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalGuard,
    MsalGuardConfiguration,
    MsalBroadcastService,
    MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation } from '@azure/msal-browser';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { environment } from '../environments/environment';
import { TimeTrackingModule } from './main/time-tracking/time-tracking.module';
import { DragulaModule } from 'ng2-dragula';
import { LEAD_PIPELINE_PUSHES, LeadPipelinePushes } from './main/agenda/shared/lead/lead-lifecycle-client.service';
import { OnboardingHandoffOverlayComponent } from './shared/common/onboarding-handoff/handoff-overlay.component';
// import { ScrollingModule } from '@angular/cdk/scrolling';
//import { SubscriptionTrialEndedModalComponent } from './main/subscription-trial-ended-modal/subscription-trial-ended-modal.component';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    wheelPropagation: true,
    suppressScrollX: true
};

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.clientID,
            redirectUri: AppConsts.appBaseHref + 'account/authenticate',
            postLogoutRedirectUri: AppConsts.appBaseHref
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: false
        }
    });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', AppConsts.scopes);
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
}

/** Lead Pipeline LP9 (PER-51): the hub's two Lead pushes for LeadLifecycleClientService (a token, so it never loads the hub). */
export function leadPipelinePushesFactory(signalr: UserSignalrService): LeadPipelinePushes {
    return { stages$: signalr.leadStageUpdates, summaries$: signalr.leadSummaryUpdates, lostReasons$: signalr.lostReasonUpdates };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: { scopes: AppConsts.scopes },
        loginFailedRoute: './login-failed'
    };
}

@NgModule({
    declarations: [
        AppComponent,
        UpgradeSubscriptionComponent
    ],
    imports: [
        ngCommon.CommonModule,
        FormsModule,
        HttpClientModule,
        // JsonpModule,
        ModalModule.forRoot(),
        TooltipModule.forRoot(),
        CollapseModule.forRoot(),
        TabsModule.forRoot(),
        FileUploadModule,
        AppRoutingModule,
        UtilsModule,
        AppCommonModule.forRoot(),
        // The directive only, not forRoot(): DragulaService is already provided at the root through
        // AppCommonModule's own forRoot() import. This is what puts [dragula] in scope for the side
        // menu's favorites list, which lives in this module's own component.
        DragulaModule,
        ServiceProxyModule,
        AngularMultiSelectModule,
        BlobModule.forRoot(),
        ReactiveFormsModule,
        InternationalPhoneNumberModule,
        AutosizeModule,
        PerfectScrollbarModule,
        NgxPopperModule.forRoot(),
        NgbPopoverModule,
        AddModule,
        SearchModule,
        NotificationModule,
        // Billing pop-ups (vf-billing-gate) and admin banners (vf-billing-banner), hosted by AppComponent.
        BillingModule,
        AddAltModule,
        CheckboxCheckedModule,
        CalendarModule,
        FoldersModule,
        ImageModule,
        DocumentBlankModule,
        PortfolioModule,
        UserMultipleModule,
        MoonModule,
        HelpModule,
        MovementModule,
        CopyFileModule,
        CloudUploadModule,
        BuildingModule,
        DebugModule,
        PhoneModule,
        UserAvatarModule,
        SettingsModule,
        LogoutModule,
        WikisModule,
        SunModule,
        CatalogModule,
        TimeModule,
        CloseModule,
        CheckmarkModule,
        ArrowUpRightModule,
        WarningModule,
        RenewModule,
        Touch_1Module,
        TrashCanModule,
        EditModule,
        IbmSecurityModule,
        DashboardModule,
           LucideAngularModule.pick({Briefcase, Calendar, CalendarPlus, Sun, Bell, Users, Phone, Moon, Settings, Search, Building, Building2, ChevronRight, File, LayoutGrid, CircleUser,
               LogOut, Plus, Star, Link, UserPlus, SquareCheck, SquareCheckBig, FileText, Upload, ChevronsRight, ChevronsLeft, ChevronDown, ChevronUp, MessageSquare, User, ArrowRight,
             PanelLeftOpen, PanelLeftClose, Mail, Video, Globe, MoreVertical, CheckSquare, Pencil, Ellipsis, Sparkles, Zap, CreditCard, BookOpen, AlertCircle, Receipt, Check, X, Shield, Cloud, Reply, Kanban}),
        TimeTrackingModule,
        // Adaptive Onboarding handoff host (standalone; placed once in AppComponent's template, filled by B-11).
        OnboardingHandoffOverlayComponent,
        MsalModule
        // ScrollingModule
    ],
    providers: [
        UserSignalrService,
        { provide: LEAD_PIPELINE_PUSHES, useFactory: leadPipelinePushesFactory, deps: [UserSignalrService] },
        {
            provide: PERFECT_SCROLLBAR_CONFIG,
            useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
        }, {
            provide: HTTP_INTERCEPTORS,
            useClass: NoCacheHeadersInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BrowserTabHeaderInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
        { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
        { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
        MsalGuard,
        MsalBroadcastService,
        MsalService
    ]
})
export class AppModule { }
